import { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@parrot-software/design-system';
import { generateFullTimeOptions } from '@reports/presentation/utils';
import { cn } from '@parrot-software/utils';

interface ReportTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  error?: boolean;
}

/**
 * ReportTimePicker - Time picker component for report filters
 *
 * Displays a dropdown with time options in 30-minute intervals
 * Value format: "HH:mm:ss" (24-hour format, e.g., "21:00:00")
 * Display format: "hh:mm AM/PM" (12-hour format, e.g., "09:00 PM")
 *
 * @param {ReportTimePickerProps} props - Component properties
 * @param {string} props.value - Selected time value in 24-hour format (HH:mm:ss)
 * @param {(value: string) => void} props.onChange - Callback when time changes (receives 24hr format)
 * @param {string} props.label - Label for the time picker
 * @param {boolean} [props.error] - Whether the picker is in error state
 * @returns {React.ReactElement} Time picker component
 *
 * @example
 * <ReportTimePicker
 *   value="21:00:00"
 *   onChange={setStartHour}
 *   label="Hora inicio"
 *   error={hasError}
 * />
 */
export const ReportTimePicker = ({ value, onChange, label, error = false }: ReportTimePickerProps) => {
  const timeOptions = useMemo(() => generateFullTimeOptions(), []);

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium">{label}</label>
      <Select onValueChange={onChange} value={value}>
        <SelectTrigger className={cn({ 'border-destructive focus:ring-destructive': error })}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="h-[240px] z-1350">
          {timeOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
